import React from 'react';
import type { FC } from 'react';

import { useCreateLinkedLayer } from '@/api/queries';
import type { Field } from '@/api/types';
import { Button, ButtonType } from '@/common/button';
import { FloatingPanel, FloatingPanelItem, FloatingPanelType } from '@/common/floating-panel';
import { TextIcon } from '@/common/text-icon';
import { toDecimal } from '@/utils';

import { useTemplateEditorStore } from '../store';
import { FieldsPanelView, type Layer, type TextLayerProps } from '../types';
import { FieldCard } from './field-card';

export interface TextLayerLinkViewProps {
    layer: Layer;
}

export const TextLayerLinkView: FC<TextLayerLinkViewProps> = ({ layer }) => {
    const fields = useTemplateEditorStore((s) => s.template?.latestVersion.fields);
    const width = useTemplateEditorStore((s) => s.fieldsPanelWidth);
    const setFields = useTemplateEditorStore((s) => s.setFields);
    const setFieldsPanelView = useTemplateEditorStore((s) => s.setFieldsPanelView);
    const setTemplateEdited = useTemplateEditorStore((s) => s.setIsEdited);

    const { mutate: createLinkedLayer } = useCreateLinkedLayer();

    const handleNewField = () => {
        setFieldsPanelView(FieldsPanelView.TextFieldEdit);
    };

    const handleLinkField = (field: Field) => {
        const textProps = layer.props as TextLayerProps;
        createLinkedLayer(
            {
                field: field.id as number,
                variant: layer.variantId,
                layerUuid: layer.id,
                name: textProps.text.substring(0, 255),
                initialValue: textProps.text,
                posX: layer.rect.x,
                posY: layer.rect.y,
                width: layer.rect.width,
                height: layer.rect.height,
                fontFamily: textProps.fontFamily,
                fontWeight: textProps.fontWeight,
                fontStyle: textProps.fontStyle,
                fontSize: textProps.fontSize,
                lineHeight: toDecimal(textProps.lineHeight, 2),
                fill: textProps.fill,
            },
            {
                onSuccess: (data) => {
                    if (!fields) throw new Error('Unexpected missing fields');

                    const updatedFields = fields?.map((f) => {
                        const updatedField = window.structuredClone(f);
                        if (f.id === field.id) {
                            updatedField.linkedLayers = updatedField.linkedLayers || [];
                            updatedField.linkedLayers.push(data);
                        }
                        return updatedField;
                    });
                    setFields(updatedFields);
                    setTemplateEdited();
                    setFieldsPanelView(FieldsPanelView.TextLayerEdit);
                },
            },
        );
    };

    return (
        <FloatingPanel
            className="pr-4 py-4 h-full flex-shrink-0 overflow-y-auto"
            type={FloatingPanelType.LIGHT}
            width={width}
        >
            <FloatingPanelItem className="flex flex-row justify-between items-center px-4">
                <div className="flex flex-row gap-2 items-center">
                    <TextIcon className="w-5 h-5 text-white" />
                    <p className="text-base text-white font-medium">Link with Text Field</p>
                </div>
                <button
                    title="Cancel"
                    className="text-white text-xs font-normal h-5"
                    type="button"
                    onClick={() => setFieldsPanelView(FieldsPanelView.Default)}
                >
                    Cancel
                </button>
            </FloatingPanelItem>

            <FloatingPanelItem className="h-full px-4">
                <div className="flex flex-col gap-3">
                    <p className="text-sm text-white font-semibold mt-2">Text Fields</p>
                    {fields?.map((field) => {
                        const isLinked = field.linkedLayers?.some((l) => l.variant === layer.variantId);
                        return (
                            <FieldCard
                                key={field.id}
                                field={field}
                                linkedLayers={isLinked && <p className="text-sm text-gray-60 my-2">Already linked to a text layer on this frame.</p>}
                                button={
                                    !isLinked ? (
                                        <Button
                                            title="Link"
                                            buttonType={ButtonType.SECONDARY}
                                            className="w-full"
                                            onClick={() => handleLinkField(field)}
                                        />
                                    ) : null
                                }
                            />
                        );
                    })}
                    <div className="flex flex-col gap-3 mt-4">
                        <Button
                            title="Create New Text Field"
                            className="w-full"
                            buttonType={ButtonType.SECONDARY}
                            onClick={handleNewField}
                        />
                        <Button
                            title="Done"
                            className="w-full"
                            buttonType={ButtonType.PRIMARY}
                            onClick={() => setFieldsPanelView(FieldsPanelView.Default)}
                        />
                    </div>
                </div>
            </FloatingPanelItem>
        </FloatingPanel>
    );
};
